import { FunctionComponent, FormEvent, useEffect, useState } from 'react';
import { Box, Select, TextField, PARTNER_ACCOUNT_STATUSES } from 'vopay';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { PartnerAccountsRequest, PartnerAccountStatus } from '@/hooks/useApi/useFetchPartnerAccounts';
import styles from './Accounts.module.scss';

type AccountsSearchFormProps = {
  country: string;
  status: PartnerAccountStatus;
  searchText: string;
  currency: string;
  onAccountSearch: (values: PartnerAccountsRequest) => void;
};

const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP'];

const AccountsSearchForm: FunctionComponent<AccountsSearchFormProps> = ({
  country,
  status,
  searchText,
  currency,
  onAccountSearch,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.Details.Accounts.SearchForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t: tStatus } = useTranslation('translations', { keyPrefix: 'Global.PartnerAccountStatus' });

  const [countryValue, setCountryValue] = useState(country);
  const [currencyValue, setCurrencyValue] = useState(currency);
  const [statusValue, setStatusValue] = useState<PartnerAccountStatus>(status);
  const [searchTextValue, setSearchTextValue] = useState(searchText);

  // Keep inputs in sync when the applied filters change externally (e.g. breadcrumb reset, back/forward).
  useEffect(() => {
    setCountryValue(country);
    setCurrencyValue(currency);
    setStatusValue(status);
    setSearchTextValue(searchText);
  }, [country, currency, status, searchText]);

  const countryOptions = [
    { label: tGlobal('Countries.CA'), value: 'Canada' },
    { label: tGlobal('Countries.USLong'), value: 'United States' },
  ];

  const currencyOptions = CURRENCIES.map((option) => ({ label: option, value: option }));

  const statusOptions = PARTNER_ACCOUNT_STATUSES.map((partnerStatus) => ({
    label: tStatus(partnerStatus),
    value: partnerStatus,
  }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onAccountSearch({
      Country: countryValue,
      Currency: currencyValue,
      VopayAccountStatus: statusValue,
      SearchText: searchTextValue,
    });
  };

  return (
    <Box ai="center" pb="md" base="8">
      <form onSubmit={handleSubmit} className={styles.searchArea}>
        <Select
          version="2"
          placeholder={t('countryPlaceholder', { defaultValue: 'Country' })}
          options={countryOptions}
          name="Country"
          onChange={(e) => setCountryValue(e.target.value)}
          value={countryValue || ''}
          showClear
          sort={false}
        />

        <Select
          version="2"
          placeholder={t('currencyPlaceholder', { defaultValue: 'Currency' })}
          options={currencyOptions}
          name="Currency"
          onChange={(e) => setCurrencyValue(e.target.value)}
          value={currencyValue || ''}
          showClear
          sort={false}
        />

        <Select
          version="2"
          placeholder={t('statusPlaceholder', { defaultValue: 'Status' })}
          options={statusOptions}
          name="Status"
          onChange={(e) => setStatusValue(e.target.value as PartnerAccountStatus)}
          value={statusValue || ''}
          showClear
          sort={false}
        />

        <TextField
          name="SearchText"
          placeholder={t('keywordPlaceholder', { defaultValue: 'Keyword' })}
          onChange={(e) => setSearchTextValue(e.target.value)}
          value={searchTextValue}
          variant="outlineShadow"
        />

        <Box jc="fe" gap="sm">
          <Button id="partner-accounts-search" type="submit" colorScheme="primary" size="small">
            {t('searchButton', { defaultValue: 'Search' })}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AccountsSearchForm;
